import { useSubmissionContextQuery } from "@qw/graphql";
import { parseDraftResourceLinkMeta } from "@qw/qw-common";
import { Button, cn, Icon } from "@qw/ui";
import type { FC } from "react";

import type { McpResourceLink } from "../chat.types";
import { useResourceLinkFirstSeen } from "../hooks/use-resource-link-first-seen";

export const GeneratedEmailDraftCard: FC<{ resource: McpResourceLink }> = ({ resource }) => {
  const handler = useResourceLinkFirstSeen(resource);
  const meta = parseDraftResourceLinkMeta(resource._meta);
  const submissionId = meta?.submissionId;

  const { data, error, loading } = useSubmissionContextQuery({
    variables: { id: submissionId ?? "" },
    skip: !submissionId,
    fetchPolicy: "cache-first",
  });

  if (!handler) {
    return null;
  }

  const subject = meta?.subject || resource.title || "No subject";
  const recipients = (meta?.to ?? []).join(", ");
  const submission = !loading && !error ? data?.submission : undefined;
  const market = submission?.appetiteProduct.carrierName;
  const lob = submission?.opportunity.selectedLinesOfBusiness.join(", ");
  const client = submission?.opportunity.insured.name;
  const showSubmissionMetadata = Boolean(market || lob || client);

  return (
    <Button
      type="button"
      variant="outline"
      onClick={() => void handler.onClick(resource)}
      className={cn(
        "h-auto w-full max-w-full justify-start gap-2 whitespace-normal px-2 py-2 text-left font-normal",
        "text-foreground hover:text-foreground"
      )}
    >
      <Icon icon="mail" className="mx-0! shrink-0 text-base!" />
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium">{subject}</span>
        {showSubmissionMetadata ? (
          <span className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            {market ? <span>Market: {market}</span> : null}
            {lob ? <span>LOB: {lob}</span> : null}
            {client ? <span>Client: {client}</span> : null}
          </span>
        ) : recipients ? (
          <span className="mt-1 block truncate text-xs text-muted-foreground">To: {recipients}</span>
        ) : null}
      </span>
    </Button>
  );
};
